import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { activityEventsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { StellarSelect } from '../components/StellarFormControls';
import {
  ArrowLeft, Clock, CheckCircle2, Edit3, MessageSquare, Upload, Download,
  LogIn, LogOut, Pause, Play, Eye, Loader2, Filter, Activity
} from 'lucide-react';
import { formatDateLong } from '../lib/dateFormat';

const EVENT_ICONS = {
  login: LogIn, logout: LogOut, session_timeout: LogOut, idle: Pause, resumed: Play,
  task_started: Play, task_paused: Pause, task_completed: CheckCircle2,
  task_opened: Eye, task_viewed: Eye,
  task_field_edit: Edit3, status_change: Activity,
  comment_added: MessageSquare, file_upload: Upload, file_download: Download,
  time_tracked: Clock,
};
const EVENT_COLORS = {
  login: 'text-green-400', logout: 'text-red-400', session_timeout: 'text-red-400',
  idle: 'text-amber-400', resumed: 'text-green-400',
  task_started: 'text-green-400', task_paused: 'text-amber-400', task_completed: 'text-green-500',
  task_field_edit: 'text-blue-400', status_change: 'text-gold',
  comment_added: 'text-purple-400', file_upload: 'text-blue-400', file_download: 'text-blue-300',
};

export default function UserActivityPage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [userName, setUserName] = useState('');

  const isSelf = userId === user?.id;

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    activityEventsAPI.userActivity(userId, days, filterType)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => {
        if (err?.response?.status === 403) {
          setData({ error: 'You do not have permission to view this user\'s activity' });
        }
        setLoading(false);
      });
  }, [userId, days, filterType]);

  // Get user name
  useEffect(() => {
    if (isSelf) { setUserName(user?.name || 'You'); return; }
    if (currentWorkspace?.id) {
      activityEventsAPI.visibleUsers(currentWorkspace.id).then(res => {
        const found = (res.data || []).find(u => u.user_id === userId);
        setUserName(found?.name || 'User');
      }).catch(() => {});
    }
  }, [userId, isSelf, user, currentWorkspace?.id]);

  const summary = data?.summary || {};
  const events = data?.events || [];

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8" data-testid="user-activity-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-display font-bold">{userName}'s Activity</h1>
          <p className="text-[12px] text-muted-foreground/50">{isSelf ? 'Your activity feed' : 'Team member activity'}</p>
        </div>
      </div>

      {data?.error ? (
        <Card className="bg-card/60"><CardContent className="p-8 text-center"><p className="text-red-400">{data.error}</p></CardContent></Card>
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Active Time', value: `${summary.active_time_minutes || 0}m`, color: 'text-green-500' },
              { label: 'Tasks Touched', value: summary.tasks_touched || 0, color: 'text-gold' },
              { label: 'Completed', value: summary.tasks_completed || 0, color: 'text-blue-400' },
              { label: 'Logins', value: summary.login_count || 0, color: 'text-foreground' },
            ].map(s => (
              <Card key={s.label} className="bg-card/60 border-border/20">
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-display font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-muted-foreground/40 mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 mb-4">
            {[{v:1,l:'Today'},{v:7,l:'7 days'},{v:30,l:'30 days'}].map(d => (
              <button key={d.v} onClick={() => setDays(d.v)} className={`px-3 py-1 rounded-lg text-[11px] font-medium transition-colors ${days === d.v ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40 hover:text-muted-foreground/60'}`}>{d.l}</button>
            ))}
            <StellarSelect
              value={filterType || '__all__'}
              onValueChange={(v) => setFilterType(v === '__all__' ? '' : v)}
              options={[
                { value: '__all__', label: 'All Events' },
                { value: 'login', label: 'Login/Logout' },
                { value: 'task_started', label: 'Task Started' },
                { value: 'task_completed', label: 'Task Completed' },
                { value: 'status_change', label: 'Status Changes' },
                { value: 'task_field_edit', label: 'Field Edits' },
                { value: 'comment_added', label: 'Comments' },
                { value: 'file_upload', label: 'File Uploads' },
                { value: 'idle', label: 'Idle' },
              ]}
              size="sm"
              className="ml-auto w-36"
            />
          </div>

          {/* Event feed */}
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gold/40" /></div>
          ) : events.length === 0 ? (
            <Card className="bg-card/60"><CardContent className="p-8 text-center text-muted-foreground/40 text-[13px]">No activity events for this period</CardContent></Card>
          ) : (
            <div className="space-y-1">
              {events.map((evt, i) => {
                const Icon = EVENT_ICONS[evt.event_type] || Activity;
                const color = EVENT_COLORS[evt.event_type] || 'text-muted-foreground/40';
                const time = new Date(evt.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
                const date = formatDateLong(evt.created_at);
                const meta = evt.metadata || {};
                let desc = evt.event_type.replace(/_/g, ' ');
                if (evt.event_type === 'task_field_edit') desc = `Changed ${meta.field}: ${meta.old_value} → ${meta.new_value}`;
                else if (evt.event_type === 'status_change') desc = `Status: ${meta.from} → ${meta.to}`;
                else if (evt.event_type === 'comment_added') desc = `Commented: "${(meta.text || '').slice(0, 60)}"`;
                else if (evt.event_type === 'file_upload') desc = `Uploaded ${meta.filename || 'file'}`;
                else if (evt.event_type === 'file_download') desc = `Downloaded ${meta.filename || 'file'}`;
                else if (evt.event_type === 'idle') desc = `Idle for ${Math.round((meta.gap_seconds || 0) / 60)}m`;
                return (
                  <div key={evt.id || i} className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-foreground/[0.02]" data-testid={`activity-event-${i}`}>
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px]">{desc}</p>
                      {evt.task_id && <p className="text-[10px] text-muted-foreground/30 mt-0.5">Task: {evt.task_id.slice(0, 8)}...</p>}
                    </div>
                    <span className="text-[10px] text-muted-foreground/30 shrink-0">{date} {time}</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
